import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { BookText, Plus, RefreshCw, Trash2, Wand2, ChevronLeft, ChevronRight, CalendarDays, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import EmptyState from "@/components/EmptyState";
import VoiceInput from "@/components/VoiceInput";
import { toast } from "sonner";

import { swallow } from "@/lib/log";
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const MOOD_COLORS = {
  joyful: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  content: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  neutral: "bg-secondary text-secondary-foreground",
  reflective: "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]",
  anxious: "bg-[hsl(var(--warning))]/20 text-[hsl(var(--warning))]",
  sad: "bg-[hsl(var(--info))]/12 text-[hsl(var(--info))]",
  angry: "bg-[hsl(var(--destructive))]/12 text-[hsl(var(--destructive))]",
  excited: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  tired: "bg-muted text-muted-foreground",
};

export default function Journal() {
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState("day");
  const [cursor, setCursor] = useState(() => ({ year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() }));
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ per_year: {}, per_month: {}, total: 0 });
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [savingEntry, setSavingEntry] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [aiSummary, setAiSummary] = useState(null); // {scope, headline, narrative, themes, mood_trend, highlights, entry_count, cached}
  const [entryDateOverride, setEntryDateOverride] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  // Contextual FAB deep-link: `?new=1` opens the new-entry dialog
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    if (sp.get("new") === "1") {
      setText(""); setOpen(true);
      const next = new URLSearchParams(sp);
      next.delete("new");
      setSp(next, { replace: true });
    }
  }, [sp, setSp]);

  const loadList = async () => {
    try {
      const params = { year: cursor.year };
      if (view !== "year") params.month = cursor.month;
      if (view === "day") params.day = cursor.day;
      const { data } = await api.get("/journal", { params });
      setEntries(data || []);
    } catch { setEntries([]); }
  };
  const loadSummary = async () => {
    try { const { data } = await api.get("/journal/summary"); setSummary(data); } catch (e) { swallow("Journal")(e); }
  };

  useEffect(() => { loadList(); }, [view, cursor.year, cursor.month, cursor.day]); // eslint-disable-line
  useEffect(() => { loadSummary(); }, []); // eslint-disable-line

  // Reset AI reflection when the scope/date changes
  useEffect(() => { setAiSummary(null); }, [view, cursor.year, cursor.month, cursor.day]);

  // ISO week helper — Monday-based week 1..53
  const isoWeek = (d) => {
    const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = (t.getUTCDay() + 6) % 7; // Mon=0..Sun=6
    t.setUTCDate(t.getUTCDate() - dayNum + 3);
    const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
    const week = 1 + Math.round(((t - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
    return { year: t.getUTCFullYear(), week };
  };

  const generateAiSummary = async (refresh = false) => {
    setAiLoading(true);
    try {
      const anchor = new Date(cursor.year, cursor.month - 1, cursor.day || 1);
      const { year, week } = isoWeek(anchor);
      const params = { scope: "week", year, week };
      if (refresh) params.refresh = true;
      const { data } = await api.get("/journal/ai-summary", { params });
      setAiSummary(data);
      if (data?.entry_count === 0) toast("No entries this week yet.");
      else if (data?.cached && !refresh) toast.success("Loaded cached summary");
      else toast.success("Weekly summary generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate summary");
    } finally { setAiLoading(false); }
  };

  const shift = (n) => {
    setCursor((c) => {
      const d = new Date(c.year, c.month - 1, c.day);
      if (view === "day") d.setDate(d.getDate() + n);
      else if (view === "month") d.setMonth(d.getMonth() + n);
      else d.setFullYear(d.getFullYear() + n);
      return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
    });
  };

  const save = async () => {
    const t = text.trim();
    if (!t) return;
    setSavingEntry(true);
    try {
      const entry_date = entryDateOverride
        ? entryDateOverride
        : view === "day"
          ? `${cursor.year}-${String(cursor.month).padStart(2,"0")}-${String(cursor.day).padStart(2,"0")}`
          : new Date().toISOString().slice(0, 10);
      const { data: saved } = await api.post("/journal", { text: t, entry_date });
      // Optimistic add so the new entry shows up instantly even if the SW
      // read cache returns a stale /api/journal snapshot on the reload below.
      if (saved && saved.id) {
        setEntries((es) => (es.some((x) => x.id === saved.id) ? es : [saved, ...es]));
      }
      setOpen(false); setText(""); setEntryDateOverride("");
      toast.success("Journal entry saved & polished");
      loadList(); loadSummary(); // background reconciliation
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingEntry(false); }
  };

  const reimprove = async (jid) => {
    try { const { data } = await api.post(`/journal/${jid}/reimprove`); setEntries((es) => es.map((x) => x.id === jid ? { ...x, ...data } : x)); toast.success("Re-polished"); }
    catch { toast.error("Failed"); }
  };

  const del = (e) => {
    setEntries((es) => es.filter((x) => x.id !== e.id));
    let undone = false;
    toast("Delete entry?", {
      description: "Undo within 5 seconds",
      duration: 5000,
      action: { label: "Undo", onClick: () => { undone = true; loadList(); } },
    });
    setTimeout(async () => {
      if (undone) return;
      try { await api.delete(`/journal/${e.id}`); loadSummary(); }
      catch { toast.error("Delete failed — restored"); loadList(); }
    }, 5100);
  };

  const label = view === "year"
    ? String(cursor.year)
    : view === "month"
      ? `${MONTHS[cursor.month - 1]} ${cursor.year}`
      : new Date(cursor.year, cursor.month - 1, cursor.day).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      <TopBar title="Journal" subtitle="Write freely — Impo polishes it" back
        right={
          <button onClick={() => { setText(""); setOpen(true); }} className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center" data-testid="journal-new-button">
            <Plus className="h-4 w-4" />
          </button>
        }
      />

      <div className="px-4 py-3 space-y-3">
        <div className="grid grid-cols-3 gap-1 rounded-full bg-secondary p-1 text-xs font-medium">
          {[["day","Day"],["month","Month"],["year","Year"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} className={`h-8 rounded-full transition ${view === k ? "bg-card shadow-sm" : "text-muted-foreground"}`} data-testid={`journal-view-${k}`}>{l}</button>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <button onClick={() => shift(-1)} className="h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center" aria-label="prev"><ChevronLeft className="h-4 w-4" /></button>
          <div className="font-display font-semibold text-sm text-center" data-testid="journal-cursor-label">{label}</div>
          <button onClick={() => shift(1)} className="h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center" aria-label="next"><ChevronRight className="h-4 w-4" /></button>
        </div>

        {/* Weekly AI summary — always available regardless of view */}
        <AiReflectionSection
          summary={aiSummary}
          loading={aiLoading}
          onGenerate={() => generateAiSummary(false)}
          onRefresh={() => generateAiSummary(true)}
        />

        {view === "year" && (
          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((m, i) => {
              const key = `${cursor.year}-${String(i+1).padStart(2,"0")}`;
              const count = summary.per_month?.[key] || 0;
              return (
                <button key={m} onClick={() => { setCursor((c) => ({ ...c, month: i+1 })); setView("month"); }} className="rounded-2xl border border-border bg-card p-3 text-center hover:bg-[hsl(var(--accent))] transition" data-testid={`journal-year-month-${i+1}`}>
                  <div className="text-xs font-medium">{m}</div>
                  <div className="text-lg font-display font-semibold">{count}</div>
                </button>
              );
            })}
          </div>
        )}

        {view !== "year" && (
          entries.length === 0 ? (
            <EmptyState icon={BookText} title="Nothing here yet" subtitle={view === "day" ? "Tap + to write today's entry." : "No entries this month."} />
          ) : (
            <div className="space-y-3">
              {entries.map((e) => (
                <div key={e.id} className="rounded-2xl border border-border bg-card p-4 impo-in" data-testid="journal-entry" onClick={() => setSelectedEntry(e)} role="button">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{e.entry_date}</span>
                      {e.mood && <Badge className={`rounded-full text-[10px] capitalize ${MOOD_COLORS[e.mood] || ""}`}>{e.mood}</Badge>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={(ev) => { ev.stopPropagation(); reimprove(e.id); }} className="h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center" title="Re-polish" data-testid="journal-reimprove"><RefreshCw className="h-3.5 w-3.5" /></button>
                      <button onClick={(ev) => { ev.stopPropagation(); del(e); }} className="h-7 w-7 rounded-full hover:bg-muted text-muted-foreground hover:text-destructive flex items-center justify-center" data-testid="journal-delete"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  {e.summary && <div className="text-xs text-muted-foreground italic mt-1.5">{e.summary}</div>}
                  <p className="text-sm mt-2 whitespace-pre-wrap line-clamp-6 leading-relaxed">{e.improved}</p>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Wand2 className="h-4 w-4 text-primary" /> New journal entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Write freely — grammar, punctuation, and structure will be polished automatically. Your original text is preserved and re-editable.</p>
            <div>
              <label className="text-xs text-muted-foreground">Entry date (backdate if needed)</label>
              <input
                type="date"
                value={entryDateOverride}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setEntryDateOverride(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="journal-entry-date-input"
              />
            </div>
            <div className="relative">
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder="what's on your mind today?" data-testid="journal-text-input" />
              <div className="absolute right-2 bottom-2">
                <VoiceInput size="sm" onTranscript={(t) => setText(t)} testId="journal-voice-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={savingEntry || !text.trim()} data-testid="journal-save-button">
              <Wand2 className="h-4 w-4 mr-1.5" /> {savingEntry ? "Polishing…" : "Save & polish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedEntry} onOpenChange={(v) => !v && setSelectedEntry(null)}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{selectedEntry?.entry_date}</DialogTitle></DialogHeader>
          {selectedEntry && (
            <div className="space-y-4">
              {selectedEntry.mood && <Badge className={`rounded-full text-[10px] capitalize ${MOOD_COLORS[selectedEntry.mood] || ""}`}>{selectedEntry.mood}</Badge>}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Polished</div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{selectedEntry.improved}</p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Original</div>
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">{selectedEntry.original}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function AiReflectionSection({ summary, loading, onGenerate, onRefresh }) {
  const hasSummary = summary && summary.summary && (summary.entry_count || 0) > 0;
  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 space-y-3"
      data-testid="journal-ai-summary-section"
    >
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">Weekly summary</div>
          <div className="text-[11px] text-muted-foreground truncate">
            {hasSummary
              ? `${summary.entry_count} entr${summary.entry_count === 1 ? "y" : "ies"} this ISO week`
              : "AI recap of the week you're viewing."}
          </div>
        </div>
        {hasSummary && summary.cached && (
          <Badge variant="outline" className="rounded-full text-[10px]">cached</Badge>
        )}
      </div>

      {!hasSummary && !loading && (
        <Button
          onClick={onGenerate}
          className="w-full h-10"
          data-testid="journal-ai-summary-generate"
        >
          <Sparkles className="h-4 w-4 mr-1.5" /> Generate weekly summary
        </Button>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-3" data-testid="journal-ai-summary-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Summarising your entries…
        </div>
      )}

      {hasSummary && !loading && (
        <div className="space-y-3" data-testid="journal-ai-summary-result">
          <div className="text-sm font-medium leading-relaxed" data-testid="journal-ai-summary-headline">
            {summary.summary.headline}
          </div>
          {summary.summary.narrative && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
              {summary.summary.narrative}
            </p>
          )}
          {(summary.summary.themes || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Themes</div>
              <div className="flex flex-wrap gap-1.5">
                {summary.summary.themes.map((t) => (
                  <Badge key={t} className="rounded-full text-[10px] capitalize">{t}</Badge>
                ))}
              </div>
            </div>
          )}
          {summary.summary.mood_trend && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Mood</div>
              <p className="text-xs text-muted-foreground">{summary.summary.mood_trend}</p>
            </div>
          )}
          {(summary.summary.highlights || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Highlights</div>
              <ul className="text-sm space-y-1 list-disc pl-5">
                {summary.summary.highlights.map((h, i) => (
                  <li key={i} className="leading-relaxed">{h}</li>
                ))}
              </ul>
            </div>
          )}
          <Button
            variant="outline"
            onClick={onRefresh}
            className="w-full h-9"
            data-testid="journal-ai-summary-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Regenerate
          </Button>
        </div>
      )}
    </div>
  );
}

